import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { CalendarDays, Star, Trophy } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

import { api } from "@/api";
import {
  EmptyState,
  ErrorState,
  HorsePortrait,
  money,
  Panel,
  Skeleton,
  TierStars,
} from "@/components/ui";
import { useRealtimeTopic } from "@/realtime/RealtimeProvider";

export default function OwnershipLeaderboardPage() {
  const [period, setPeriod] = useState<"weekly" | "season">("weekly");
  useRealtimeTopic("world.standings");
  useRealtimeTopic("world.jackpot");

  const season = useQuery({
    queryKey: ["season", "current"],
    queryFn: api.currentSeason,
  });
  const board = useQuery({
    queryKey: [
      "leaderboards",
      "ownership",
      period,
      season.data?.season.ordinal,
    ],
    queryFn: () =>
      api.ownershipLeaderboard(period, season.data?.season.ordinal),
    enabled: season.isSuccess,
    refetchInterval: 15_000,
  });

  return (
    <>
      <Panel className="leaderboard-hero">
        <div>
          <span>
            Ownership leaderboard <b>Live</b>
          </span>
          <h1>Racehorse standings</h1>
          <p>
            Ranked by share-weighted points. Rank is cosmetic; projected money
            is pari-mutuel over every point.
          </p>
          <small>
            <Trophy aria-hidden />{" "}
            {season.data?.season.name ?? "Current season"}
          </small>
        </div>
        <div className="leaderboard-tabs">
          <button
            className={clsx(period === "weekly" && "is-active")}
            onClick={() => setPeriod("weekly")}
            type="button"
          >
            Weekly
          </button>
          <button
            className={clsx(period === "season" && "is-active")}
            onClick={() => setPeriod("season")}
            type="button"
          >
            Season
          </button>
        </div>
      </Panel>

      <Panel className="prize-pool-banner">
        <div>
          <span>Projected {period} pool</span>
          <strong>{money(board.data?.poolCents)}</strong>
        </div>
        <div>
          <CalendarDays aria-hidden />
          <span>
            {board.data
              ? `${(board.data.totalPointsBp / 10000).toLocaleString()} active points`
              : "Loading points…"}
          </span>
        </div>
      </Panel>

      {board.isPending ? (
        <Skeleton rows={10} variant="leaderboard" />
      ) : board.error ? (
        <ErrorState error={board.error} retry={() => void board.refetch()} />
      ) : board.data.rows.length ? (
        <Panel className="ownership-board">
          <header>
            <span>Rank</span>
            <span>Horse</span>
            <span>Owners</span>
            <span>Record</span>
            <span>Form</span>
            <span>Points</span>
            <span>Projected</span>
          </header>
          {board.data.rows.map((row) => (
            <Link
              className={clsx(
                "ownership-row",
                row.owners.some((owner) => owner.isViewer) && "is-viewer",
              )}
              href={`/app/player/horse/${row.horse.horseId}`}
              key={row.horse.horseId}
            >
              <b className={`rank rank--${row.rank}`}>{row.rank}</b>
              <div className="ownership-row__horse">
                <HorsePortrait compact horse={row.horse} />
                <span>
                  <strong>{row.horse.name}</strong>
                  <TierStars
                    rating={row.horse.starRating}
                    tier={row.horse.tier}
                  />
                  <small>
                    {row.horse.bloodline} · {row.horse.career.races} races
                  </small>
                </span>
              </div>
              <span className="ownership-row__owners">
                {row.owners.slice(0, 2).map((owner) => (
                  <i key={owner.playerId} title={owner.displayName}>
                    {owner.displayName.slice(0, 2).toUpperCase()}
                  </i>
                ))}
                {row.owners.some((owner) => owner.isViewer) ? (
                  <b>Yours</b>
                ) : null}
              </span>
              <span>
                {row.horse.career.wins}W · {row.horse.career.places}P ·{" "}
                {row.horse.career.shows}S
              </span>
              <span className="form-boxes">
                {row.horse.career.form
                  .slice(0, 5)
                  .split("")
                  .map((value, index) => (
                    <i key={index}>{value}</i>
                  ))}
              </span>
              <strong>{row.points.toLocaleString()}</strong>
              <span className="ownership-row__prize">
                <strong>{money(row.projectedCents)}</strong>
                {row.viewerProjectedCents ? (
                  <small>+{money(row.viewerProjectedCents)} yours</small>
                ) : null}
              </span>
            </Link>
          ))}
        </Panel>
      ) : (
        <EmptyState title="No ownership points in this period" />
      )}

      {board.data?.viewer ? (
        <Panel className="viewer-standing">
          <Star aria-hidden />
          <strong>Your stable</strong>
          <dl>
            <div>
              <dt>Best position</dt>
              <dd>#{board.data.viewer.bestRank ?? "—"}</dd>
            </div>
            <div>
              <dt>Total points</dt>
              <dd>{board.data.viewer.totalPoints.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Projected payout</dt>
              <dd>{money(board.data.viewer.projectedCents)}</dd>
            </div>
            <div>
              <dt>Shares held</dt>
              <dd>{board.data.viewer.sharesHeld}</dd>
            </div>
          </dl>
          <Link href="/app/player">View stable</Link>
        </Panel>
      ) : null}
    </>
  );
}
